import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate, withLocalize } from 'react-localize-redux';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import Tooltip from '../../components/molecules/Tooltip';
import { formatFileSize } from '../../utils/numbers';
import { ATTACHMENT_KINDS } from '../constants';
import { ATTACHMENT_STATUS } from '../hooks/useAttachments';
import { fetchArtifactBlob } from '../hooks/useArtifactContent';
import css from './AttachmentChip.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT.ATTACHMENTS';

const KIND_ICONS = {
    [ATTACHMENT_KINDS.IMAGE]: 'photo',
    [ATTACHMENT_KINDS.VIDEO]: 'video',
    [ATTACHMENT_KINDS.CSV]: 'file-spreadsheet',
    [ATTACHMENT_KINDS.XLSX]: 'file-spreadsheet',
    [ATTACHMENT_KINDS.PDF]: 'file-text',
};

// Not a bare <img src>: thumbnails need interactiveServiceFetch's auth handling.
const useImagePreview = (previewUrl, contentUrl, isImage) => {
    const [fetchedUrl, setFetchedUrl] = useState(null);

    useEffect(() => {
        if (!isImage || previewUrl || !contentUrl) return undefined;
        let objectUrl = null;
        const controller = new AbortController();
        setFetchedUrl(null);
        fetchArtifactBlob(contentUrl, { signal: controller.signal })
            .then(blob => {
                if (controller.signal.aborted) return;
                objectUrl = URL.createObjectURL(blob);
                setFetchedUrl(objectUrl);
            })
            .catch(() => {});
        return () => {
            controller.abort();
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [isImage, previewUrl, contentUrl]);

    return previewUrl || fetchedUrl;
};

const AttachmentChip = ({
    name,
    kind = null,
    sizeBytes = null,
    status = null,
    errorKey = null,
    errorMessage = null,
    previewUrl = null,
    contentUrl = null,
    onRemove = null,
    onRetry = null,
    large = false,
    translate,
}) => {
    const isImage = kind === ATTACHMENT_KINDS.IMAGE;
    const imageUrl = useImagePreview(previewUrl, contentUrl, isImage);
    const isError = status === ATTACHMENT_STATUS.ERROR;

    if (large && isImage && !isError) {
        return (
            <div className={css.imagePreview}>
                {imageUrl ? (
                    <img className={css.previewImage} src={imageUrl} alt={name} />
                ) : (
                    <div className={css.previewPlaceholder}>
                        <Icon name={KIND_ICONS[kind]} size={IconSizes.MEDIUM} interactive={false} />
                    </div>
                )}
                <div className={css.caption}>
                    <Tooltip title={name} contentClass={css.tooltipContent} wrapperClass={css.captionNameWrapper}>
                        <span className={css.captionName}>{name}</span>
                    </Tooltip>
                    <span className={css.captionSize}>{formatFileSize(sizeBytes)}</span>
                </div>
            </div>
        );
    }

    return (
        <Tooltip title={name} contentClass={css.tooltipContent} wrapperClass={css.tooltipWrapper}>
            <div className={classNames(css.attachmentChip, { [css.error]: isError, [css.large]: large })}>
                {isImage && imageUrl ? (
                    <img className={css.thumbnail} src={imageUrl} alt={name} />
                ) : (
                    <Icon
                        name={KIND_ICONS[kind] || 'file'}
                        size={large ? IconSizes.MEDIUM : IconSizes.SMALL}
                        variant={isError ? IconVariants.ERROR : undefined}
                        className={css.kindIcon}
                        interactive={false}
                    />
                )}
                <div className={css.details}>
                    <span className={css.name}>{name}</span>
                    {isError ? (
                        <span className={css.errorText}>
                            {errorMessage || (
                                <Translate
                                    id={`${TRANSLATION_PREFIX}.${(errorKey || 'upload_failed').toUpperCase()}`}
                                />
                            )}
                        </span>
                    ) : (
                        <span className={css.size}>{formatFileSize(sizeBytes)}</span>
                    )}
                </div>
                {status === ATTACHMENT_STATUS.UPLOADING && (
                    <Icon name="loader-2" size={IconSizes.SMALL} className={css.spinner} interactive={false} />
                )}
                {isError && onRetry && (
                    <button
                        type="button"
                        className={css.retryButton}
                        onClick={onRetry}
                        aria-label={translate(`${TRANSLATION_PREFIX}.RETRY_LABEL`)}
                    >
                        <Icon name="refresh" size={IconSizes.X_SMALL} interactive={false} />
                    </button>
                )}
                {onRemove && (
                    <button
                        type="button"
                        className={css.removeButton}
                        onClick={onRemove}
                        aria-label={translate(`${TRANSLATION_PREFIX}.REMOVE_LABEL`)}
                    >
                        <Icon name="x" size={IconSizes.X_SMALL} interactive={false} />
                    </button>
                )}
            </div>
        </Tooltip>
    );
};

AttachmentChip.propTypes = {
    name: PropTypes.string.isRequired,
    kind: PropTypes.string,
    sizeBytes: PropTypes.number,
    status: PropTypes.string,
    errorKey: PropTypes.string,
    errorMessage: PropTypes.string,
    previewUrl: PropTypes.string,
    contentUrl: PropTypes.string,
    onRemove: PropTypes.func,
    onRetry: PropTypes.func,
    large: PropTypes.bool,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(AttachmentChip);
